import React, { useState, useEffect } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import ColumnFilterSelector from 'components/filters/ColumnFilterSelector';
import OrderBySelector from 'components/filters/OrderBySelector';
import LimitSelector from 'components/filters/LimitSelector';
import ListCutterTargetSelector from 'components/bigquery/ListCutterTargetSelector';
import ViewTableInBigQueryButton from 'components/bigquery/ViewTableInBigQueryButton';

const ListCutterJobSelectFilters = ({ 
  availableColumns = [],
  selectedColumnFilters = [],
  onColumnsChange,
  onTargetDetailsChange,
  sourceDataset, 
  sourceTable,
  sourceTableSchema = [],
  projectId,
  initialOrderByConditions = [],
  initialLimitValue = null,
  initialTargetDataset = '',
  initialTargetTable = '',
  limitError,
  onValidationErrorChange
}) => {
  const [currentTargetDataset, setCurrentTargetDataset] = useState(initialTargetDataset);
  const [currentTargetTable, setCurrentTargetTable] = useState(initialTargetTable);
  const [limitValue, setLimitValue] = useState(initialLimitValue);
  const [orderByConditions, setOrderByConditions] = useState(initialOrderByConditions);
  const [hasFilterValidationErrors, setHasFilterValidationErrors] = useState(false);
  const [tableExistsWarning, setTableExistsWarning] = useState(null);
  const [isDatasetReadOnly, setIsDatasetReadOnly] = useState(false);

  const handleTargetDetailsChange = (dataset, table, isValid = true, warning = null) => {
    setCurrentTargetDataset(dataset);
    setCurrentTargetTable(table);
    setTableExistsWarning(warning);
    setIsDatasetReadOnly(!isValid);
    
    onTargetDetailsChange(
      selectedColumnFilters || [],
      dataset,
      table,
      orderByConditions,
      Boolean(limitValue),
      limitValue,
      warning
    );
    
    const hasValidationError = !dataset || !isValid;
    
    if (onValidationErrorChange) {
      onValidationErrorChange(
        hasFilterValidationErrors || 
        Boolean(limitError) || 
        hasValidationError
      );
    }
  };

  const handleOrderByChange = (newOrderByConditions) => {
    setOrderByConditions(newOrderByConditions);
    onTargetDetailsChange(
      selectedColumnFilters || [],
      currentTargetDataset,
      currentTargetTable,
      newOrderByConditions,
      Boolean(limitValue),
      limitValue,
      tableExistsWarning
    );
  };

  const handleLimitValueChange = (value) => {
    setLimitValue(value);
    onTargetDetailsChange(
      selectedColumnFilters || [],
      currentTargetDataset,
      currentTargetTable,
      orderByConditions,
      Boolean(value),
      value,
      tableExistsWarning
    );
  };

  const handleFilterValidationErrors = (hasErrors) => {
    setHasFilterValidationErrors(hasErrors);
    
    if (onValidationErrorChange) {
      onValidationErrorChange(
        hasErrors || 
        Boolean(limitError) || 
        !currentTargetDataset || 
        isDatasetReadOnly
      );
    }
  };

  useEffect(() => {
    setOrderByConditions(initialOrderByConditions);
    setLimitValue(initialLimitValue);
  }, [initialOrderByConditions, initialLimitValue]);

  useEffect(() => {
    if (onValidationErrorChange) {
      onValidationErrorChange(
        hasFilterValidationErrors || 
        Boolean(limitError) || 
        !currentTargetDataset || 
        isDatasetReadOnly
      );
    }
  }, [
    hasFilterValidationErrors, 
    limitError, 
    currentTargetDataset, 
    isDatasetReadOnly, 
    onValidationErrorChange
  ]);

  return (
    <Box>
      <Typography variant="h1" sx={{ mb: 2 }}>
        Let's Cut a List
      </Typography>
      
      <ListCutterTargetSelector
        initialDataset={initialTargetDataset}
        initialTable={initialTargetTable}
        onDetailsChange={handleTargetDetailsChange}
        sourceTable={sourceTable}
      />
      
      <Typography variant="h3" sx={{ mb: 2, mt: 5 }}>
        Set Filters
      </Typography>
      
      <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', mb: 2 }}>
        Select columns to use to filter records in your list. If you are feeling CREATIVE, you may enter a SQL WHERE clause instead of using the friendly filter UI. Note that not all field types have a friendly filter UI available.
      </Typography>
      
      {sourceDataset && sourceTable && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            Need to check the table schema?
          </Typography>
          <Box sx={{ ml: 1 }}>
            <ViewTableInBigQueryButton 
              projectId={projectId}
              datasetId={sourceDataset}
              tableId={sourceTable}
              buttonText="View in BigQuery"
              variant="outlined"
              color="primary"
              size="small"
              sx={{ 
                fontSize: '0.75rem',
                py: 0.5,
                px: 1.5,
                borderRadius: 1,
                minWidth: 'auto'
              }}
            />
          </Box>
        </Box>
      )}
      
      <ColumnFilterSelector
        availableColumns={availableColumns}
        selectedColumnFilters={selectedColumnFilters}
        onChange={onColumnsChange}
        onValidationErrorChange={handleFilterValidationErrors}
        sourceTableSchema={sourceTableSchema}
        projectId={projectId}
        datasetId={sourceDataset}
        tableId={sourceTable}
      />

      <Box sx={{ mt: 6, mb: 4 }}>
        <Divider />
      </Box>

      <Typography variant="h3" sx={{ mb: 2, mt: 5 }}>
        Limit Rows
      </Typography>
      <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', mb: 3 }}>
        Note: Limiting the number of rows in the new list does not reduce the cost of the BigQuery job. Learn more about BigQuery compute costs and best practices <a href="https://cloud.google.com/bigquery/docs/best-practices-costs#estimate-query-costs" target="_blank" rel="noopener noreferrer">here</a>.
      </Typography>

      <LimitSelector
        limitValue={limitValue}
        onLimitValueChange={handleLimitValueChange}
        error={limitError}
      />

      <Box sx={{ mt: 4 }}>
        <OrderBySelector
          availableColumns={sourceTableSchema.map(col => col.name)}
          orderByConditions={orderByConditions}
          onChange={handleOrderByChange}
        />
      </Box>
    </Box>
  );
};

export default ListCutterJobSelectFilters; 